import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { signOut } from 'firebase/auth';
import { 
  FaBars, 
  FaTimes, 
  FaHome, 
  FaUtensils, 
  FaUsers, 
  FaChartBar, 
  FaFileInvoice, 
  FaCalendarAlt, 
  FaUserFriends, 
  FaCreditCard, 
  FaSignOutAlt,
  FaCog,
  FaChevronDown,
  FaChevronRight
} from 'react-icons/fa';
import AddDishForm from './AddDishForm';
import ViewMenuItems from './ViewMenuItems';
import AddServerForm from './AddServerForm';
import AddCookForm from './AddCookForm';
import AddBarForm from './AddBarForm';
import Statistics from './Statistics';
import AdminHome from './AdminHome';
import WeekInvoices from './WeekInvoices';
import ManageSchedule from './schedule/ManageSchedule';
import WorkHours from './WorkHours';
import ClientId from './ClientId';
import Avis from './Avis';
import MyPaiement from './MyPaiement';
import ComparaisonDate from './ComparaisonDate';
import ManageTables from './ManageTables';
import HandleSupplements from './HandleSupplement';
import './AdminDashboard.css';
import Footer from '../Footer';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const menuId = "ZifkqWN99oRgRNM0Nisz";
  const [isPageActive, setIsPageActive] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [activeSection, setActiveSection] = useState('accueil');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const fetchPageStatus = useCallback(async () => {
    try {
      const docRef = doc(db, 'settings', 'pageStatus');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setIsPageActive(docSnap.data().isActive);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'état de la page :', error);
    }
  }, []);
  
  const togglePageStatus = async () => {
    try {
      const newStatus = !isPageActive;
      const docRef = doc(db, 'settings', 'pageStatus');
      await updateDoc(docRef, { isActive: newStatus });
      setIsPageActive(newStatus);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'état de la page :', error);
    }
  };
  
  useEffect(() => {
    fetchPageStatus();
  }, [fetchPageStatus]);

  const getSectionFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get('section') || 'accueil';
  }, [location]);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const userRef = doc(db, 'identifications', user.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            if (userData.role !== 'admin') {
              setIsAuthorized(false);
              setTimeout(() => navigate('/login'), 3000);
            }
          } else {
            setIsAuthorized(false);
            setTimeout(() => navigate('/login'), 3000);
          }
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du rôle :', error);
        setIsAuthorized(false);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    fetchUserRole();
  }, [navigate]);

  useEffect(() => {
    setActiveSection(getSectionFromUrl());
  }, [location, getSectionFromUrl]);

  const adminNavigationItems = [
    {
      id: 'accueil',
      label: 'Accueil',
      icon: FaHome,
      isDirectLink: true
    },
    {
      id: 'menu',
      label: 'Mon Menu',
      icon: FaUtensils,
      subItems: [
        { path: 'addDish', label: 'Ajouter un plat', icon: FaUtensils },
        { path: 'viewMenu', label: 'Gérer mon menu', icon: FaCog },
        { path: 'handleSupplement', label: 'Gérer mes suppléments', icon: FaCog }
      ]
    },
    {
      id: 'workers',
      label: 'Mon Équipe',
      icon: FaUsers,
      subItems: [
        { path: 'manageServers', label: 'Gérer les serveurs', icon: FaUsers },
        { path: 'manageCooks', label: 'Gérer les cuisiniers', icon: FaUsers },
        { path: 'manageBarmaids', label: 'Gérer les barmans', icon: FaUsers }
      ]
    },
    {
      id: 'statistics',
      label: 'Statistiques',
      icon: FaChartBar,
      subItems: [
        { path: 'statistics', label: 'Voir les statistiques', icon: FaChartBar },
        { path: 'comparaison', label: 'Faire mes comparaisons', icon: FaChartBar }
      ]
    },
    {
      id: 'invoices',
      label: 'Factures',
      icon: FaFileInvoice,
      subItems: [
        { path: 'weekInvoices', label: 'Visualiser mes factures', icon: FaFileInvoice }
      ]
    },
    {
      id: 'planning',
      label: 'Planning',
      icon: FaCalendarAlt,
      subItems: [
        { path: 'manageSchedule', label: 'Gérer le planning', icon: FaCalendarAlt },
        { path: 'WorkHours', label: 'Heures de travail', icon: FaCalendarAlt }
      ]
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: FaUserFriends,
      subItems: [
        { path: 'myClient', label: 'Voir les clients', icon: FaUserFriends },
        { path: 'avis', label: 'Mes avis', icon: FaUserFriends },
        { path: 'ManageTables', label: 'Gérer mes tables', icon: FaUserFriends }
      ]
    },
    {
      id: 'paiement',
      label: 'Paiements',
      icon: FaCreditCard,
      subItems: [
        { path: 'myPaiement', label: 'Voir les paiements en attente', icon: FaCreditCard }
      ]
    }
  ];

  const handleSectionChange = (path) => {
    setActiveSection(path);
    navigate(`/admin-dashboard?section=${path}`);
    setIsMobileMenuOpen(false);
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      // Fermer toutes les autres sections et basculer la section actuelle
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      newState[sectionId] = !prev[sectionId];
      return newState;
    });
  };

  if (!isAuthorized) {
    return (
      <div className="admin-unauthorized-container">
        <div className="admin-unauthorized-popup">
          <h2>Accès Refusé</h2>
          <p>Vous n'êtes pas autorisé à accéder à cette page.</p>
          <p>Redirection en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Header avec menu burger */}
      <header className="admin-dashboard-header">
        <div className="admin-header-content">
          <div className="admin-logo-section">
            <h1 className="admin-logo">SkanEat Admin</h1>
          </div>
          
          <button 
            className={`admin-mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      {/* Navigation mobile */}
      <nav className={`admin-mobile-navigation ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="admin-mobile-nav-content">
          {adminNavigationItems.map((item) => (
            <div key={item.id} className="admin-mobile-nav-item">
              {item.isDirectLink ? (
                <button
                  className={`admin-mobile-nav-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => handleSectionChange(item.id)}
                >
                  <item.icon className="admin-nav-icon" />
                  <span>{item.label}</span>
                </button>
              ) : (
                <>
                  <button
                    className={`admin-mobile-nav-link admin-mobile-nav-toggle ${expandedSections[item.id] ? 'expanded' : ''}`}
                    onClick={() => toggleSection(item.id)}
                  >
                    <item.icon className="admin-nav-icon" />
                    <span>{item.label}</span>
                    {expandedSections[item.id] ? <FaChevronDown /> : <FaChevronRight />}
                  </button>
                  
                  <div className={`admin-mobile-submenu ${expandedSections[item.id] ? 'expanded' : ''}`}>
                    {item.subItems.map((subItem) => (
                      <button
                        key={subItem.path}
                        className={`admin-mobile-submenu-link ${activeSection === subItem.path ? 'active' : ''}`}
                        onClick={() => handleSectionChange(subItem.path)}
                      >
                        <subItem.icon className="admin-submenu-icon" />
                        <span>{subItem.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
          
          <div className="admin-mobile-nav-divider"></div>
          
          <button className="admin-mobile-logout-btn" onClick={handleLogoutClick}>
            <FaSignOutAlt className="admin-nav-icon" />
            <span>Se déconnecter</span>
          </button>
        </div>
      </nav>

      {/* Navigation desktop */}
      <aside className="admin-desktop-sidebar">
        <div className="admin-sidebar-content">
          <div className="admin-sidebar-header">
            <h2 className="admin-sidebar-title">Navigation</h2>
          </div>
          
          <nav className="admin-sidebar-nav">
            {adminNavigationItems.map((item) => (
              <div key={item.id} className="admin-sidebar-item">
                {item.isDirectLink ? (
                  <button
                    className={`admin-sidebar-link ${activeSection === item.id ? 'active' : ''}`}
                    onClick={() => handleSectionChange(item.id)}
                  >
                    <item.icon className="admin-sidebar-icon" />
                    <span>{item.label}</span>
                  </button>
                ) : (
                  <>
                    <button
                      className={`admin-sidebar-link admin-sidebar-toggle ${expandedSections[item.id] ? 'expanded' : ''}`}
                      onClick={() => toggleSection(item.id)}
                    >
                      <item.icon className="admin-sidebar-icon" />
                      <span>{item.label}</span>
                      {expandedSections[item.id] ? <FaChevronDown /> : <FaChevronRight />}
                    </button>
                    
                    <div className={`admin-sidebar-submenu ${expandedSections[item.id] ? 'expanded' : ''}`}>
                      {item.subItems.map((subItem) => (
                        <button
                          key={subItem.path}
                          className={`admin-sidebar-submenu-link ${activeSection === subItem.path ? 'active' : ''}`}
                          onClick={() => handleSectionChange(subItem.path)}
                        >
                          <subItem.icon className="admin-sidebar-submenu-icon" />
                          <span>{subItem.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </nav>
          
          <div className="admin-sidebar-footer">
            <button className="admin-sidebar-logout-btn" onClick={handleLogoutClick}>
              <FaSignOutAlt className="admin-sidebar-icon" />
              <span>Se déconnecter</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Contenu principal */}
      <main className="admin-main-content">
        <div className="admin-content-wrapper">
          {/* Contrôle de la page principale */}
          <div className="admin-page-control">
            <div className="admin-page-control-content">
              <h3 className="admin-page-control-title">État du Menu Digital</h3>
              <button 
                onClick={togglePageStatus} 
                className={`admin-page-control-btn ${isPageActive ? 'active' : 'inactive'}`}
              >
                {isPageActive ? 'Menu Digital Actif' : 'Menu Digital Inactif'}
              </button>
            </div>
          </div>

          {/* Contenu des sections */}
          <div className="admin-section-content">
            {activeSection === 'accueil' && <AdminHome />}
            {activeSection === 'addDish' && <AddDishForm menuId={menuId} />}
            {activeSection === 'viewMenu' && <ViewMenuItems menuId={menuId} />}
            {activeSection === 'manageServers' && <AddServerForm />}
            {activeSection === 'manageCooks' && <AddCookForm />}
            {activeSection === 'manageBarmaids' && <AddBarForm />}
            {activeSection === 'statistics' && <Statistics />}
            {activeSection === 'weekInvoices' && <WeekInvoices />}
            {activeSection === 'manageSchedule' && <ManageSchedule />}
            {activeSection === 'WorkHours' && <WorkHours />}
            {activeSection === 'myClient' && <ClientId />}
            {activeSection === 'avis' && <Avis />}
            {activeSection === 'myPaiement' && <MyPaiement />}
            {activeSection === 'comparaison' && <ComparaisonDate />}
            {activeSection === 'ManageTables' && <ManageTables />}
            {activeSection === 'handleSupplement' && <HandleSupplements menuId={menuId} />}
          </div>
        </div>
      </main>

      {/* Overlay pour mobile */}
      {isMobileMenuOpen && (
        <div className="admin-mobile-overlay" onClick={toggleMobileMenu}></div>
      )}

      {/* Confirmation de déconnexion */}
      {showLogoutConfirm && (
        <div className="admin-logout-overlay">
          <div className="admin-logout-modal">
            <div className="admin-logout-header">
              <h3>Confirmation de déconnexion</h3>
            </div>
            <div className="admin-logout-body">
              <p>Êtes-vous sûr de vouloir vous déconnecter ?</p>
            </div>
            <div className="admin-logout-actions">
              <button onClick={handleLogoutConfirm} className="admin-logout-confirm-btn">
                Confirmer
              </button>
              <button onClick={handleLogoutCancel} className="admin-logout-cancel-btn">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminDashboard;